/** @forge-header
 * @id          useGameFairView
 * @role        One game's fair-value view | Unabated consensus + OP odds, column-aligned to the GameRow
 * @pattern     hook
 * @purity      none
 * @units       cents / tenths
 * @money-path  no
 * @upstream    stores/marketStore:useConsensusFor,useConsensusTrendFor,useOpOddsFor,useOpTrendFor,useOpLastAnchorFairFor | stores/gameStatesStore:useGameStateFor | lib/opOdds:buildOpFairView
 * @downstream  ValueDashboard/index:ExpandedGamePanel | Cockpit/live/FairRow
 * @exports     useGameFairView:hook | GameFairView:interface | GameFairUnabated:interface
 */

// Factored out of ValueDashboard's ExpandedGamePanel (2026-08-21) so the
// Cockpit's live feed and the ticket read ONE fair view — same swap rule, same
// key vocabulary, same showOpOdds gate. No behavior change for the panel.
import {
  useConsensusFor,
  useConsensusTrendFor,
  useOpOddsFor,
  useOpTrendFor,
  useOpLastAnchorFairFor,
} from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { buildOpFairView, type OpFairView } from '@/lib/opOdds';
import { baseGameKey, toHydraGameKeyLive } from '@/services/analyticsGameKey';
import type { ConsensusTrendState } from '@/lib/consensusTrend';
import type { GameRow } from '@/views/ValueDashboard/types';

/** Shape GameDetailPanel's `unabated` prop accepts — already aligned to the
 *  row's away/home columns. */
export interface GameFairUnabated {
  awayCents: number | null;
  homeCents: number | null;
  awayTenths: number | null;
  homeTenths: number | null;
  trend: ConsensusTrendState | null;
  swapped: boolean;
  sharpBookUsed: boolean | null;
}

export interface GameFairView {
  unabated: GameFairUnabated | null;
  opOdds: OpFairView | null;
  opOddsTrend: ConsensusTrendState | null;
  gameState: ReturnType<typeof useGameStateFor>;
}

export function useGameFairView(game: GameRow): GameFairView {
  // For spread/total synth keys (`<eventKey>|spread|<ticker>`) we want
  // the eventKey portion for the gameStates lookup, not the synth key
  // itself. Moneyline rows fall through unchanged. Shared with GameOpenOrders'
  // order matching so the synth→base boundary is defined in exactly one place.
  const lookupBase = baseGameKey(game.key);
  // Live (doubleheader-safe) key — the poll and the WS consensus must agree on
  // the v2 key or a doubleheader's two rows share one score.
  const hydraKey = toHydraGameKeyLive(lookupBase);
  const gameState = useGameStateFor(hydraKey);
  // Prefer the live WS consensus (sub-second freshness via the `unabated`
  // channel, push on ≥1¢ shift). Consensus is cached per the doubleheader-safe
  // v2 key, which IS the row key, so pass `lookupBase` straight through.
  const consensusKey = lookupBase || null;
  const liveConsensus = useConsensusFor(consensusKey);
  const liveConsensusTrend = useConsensusTrendFor(consensusKey);
  // OP odds — same key vocabulary as consensus, separate store map. Gated on
  // the user toggle so switching showOpOdds off hides the row along with the
  // dashboard column.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const opSlice = useOpOddsFor(showOpOdds ? consensusKey : null);
  // The ONE fair-view builder (rules 4 + 6c) — no inline copy of the swap
  // rule, no local fair-value math.
  const opLastAnchor = useOpLastAnchorFairFor(showOpOdds ? consensusKey : null);
  const opOdds = opSlice ? buildOpFairView(opSlice, game.homeCode, opLastAnchor) : null;
  const opOddsTrend = useOpTrendFor(showOpOdds ? consensusKey : null);

  let unabated: GameFairUnabated | null = null;
  // WS is the sole source of consensus — hydra moved it off the REST
  // gameStates response as of a37c761.
  const c = liveConsensus;
  if (c) {
    // Same swap rule the GameDetail page applies — Unabated's awayTeam can
    // land in our homeCode slot after canonical-key alpha-sort.
    const swapped = c.awayTeam === game.homeCode;
    unabated = {
      awayCents: swapped ? c.homeFairProbCents : c.awayFairProbCents,
      homeCents: swapped ? c.awayFairProbCents : c.homeFairProbCents,
      // Tenth-accurate value when hydra sends it (integer cents remain the
      // fallback + the glow key).
      awayTenths: (swapped ? c.homeFairProbTenths : c.awayFairProbTenths) ?? null,
      homeTenths: (swapped ? c.awayFairProbTenths : c.homeFairProbTenths) ?? null,
      // Trend keeps Unabated's own slots; consumers re-apply `swapped`.
      trend: liveConsensusTrend,
      swapped,
      sharpBookUsed: c.sharpBookUsed ?? null,
    };
  }

  return { unabated, opOdds, opOddsTrend, gameState };
}
